import React from 'react';
import { GetServerSideProps } from 'next';

import { fetchNavHTML } from 'libs/server/nav';
import { fetchUser } from 'libs/server/users';
import { UserContext } from '@zola-helpers/client/dist/es/@types';
import { ExpertAdviceFooterType } from 'libs/contentful/types';

import { BreadcrumbsV2 } from '@zola/zola-ui/src/components/BreadcrumbsV2';
import { useIsPostAuthNav3MobileOrDesktop } from 'hooks/useIsPostAuthNav3Active';
import Head from 'next/head';
import logger from '@zola-helpers/server/dist/es/logger';
import { fetchStateData, StateDataType } from 'libs/server/expertAdvice';
import { toKebabCase } from 'libs/universal/utils/text';
import { getCachedExpertAdviceFooterData } from 'libs/contentful/api';

import Nav from 'components/Nav';
import { SectionContainer } from 'components/expert-advice';
import ExpertAdviceFooter from 'components/expert-advice/ExpertAdviceFooter';
import PreauthFooterV3 from '@zola/zola-ui/src/components/PreAuthV3/PreauthFooterV3';

import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';

import { PageWrapper } from 'components/LP/v3/PageWrapper.styles';
import { BreadcrumbsContainer, PostBody } from 'styles/expertAdviceShared.styles';
import {
  CrossSellContainer,
  HeroContent,
  HeroImage,
  HeroWrapper,
  StateContainer,
  StateLink,
  Subhead,
} from 'styles/pages/BestVenuesStatePage.styles';

import Venues from '../../../../public/static-preauth/images/expert-advice/Venues.png';

type BestVenuesStatePageProps = {
  expertAdviceFooter: ExpertAdviceFooterType;
  navHTML: string;
  userCtx: UserContext;
  data: StateDataType[];
};

const BestVenuesStatePage: React.FC<BestVenuesStatePageProps> = ({
  expertAdviceFooter,
  navHTML,
  userCtx,
  data,
}) => {
  const isPostAuthNav3Enabled = useIsPostAuthNav3MobileOrDesktop(userCtx);

  const currentSelectionLabel = 'Top wedding venues';
  const breadcrumbs = [{ to: '/expert-advice', label: 'Expert advice' }];
  const stateData = [] as string[];
  if (Array.isArray(data)) {
    data?.forEach((datum: StateDataType) => {
      if (datum?.name) {
        stateData.push(datum?.name);
      }
    });
  }
  const uniqueStates = [...new Set(stateData)];
  const sortedUniqueStates = Array.from(uniqueStates).sort((a, b) => a.localeCompare(b));
  return (
    <>
      <Head>
        <title>Top wedding venues on Zola</title>
        <meta
          name="description"
          content="With so many venue options available, this step of wedding planning can feel
                  overwhelming! We rounded up the most-loved venues by city to get you started."
        />
      </Head>
      <Nav
        activePrimaryLink={PrimaryNavIdentifier.ADVICE}
        navHTML={navHTML}
        showPromo
        user={userCtx}
        disableSecondary
        disablePrimaryNavCollapse
      />
      <PageWrapper isPostAuthNav3={isPostAuthNav3Enabled}>
        <BreadcrumbsContainer>
          <BreadcrumbsV2 currentSelectionLabel={currentSelectionLabel} linkItems={breadcrumbs} />
        </BreadcrumbsContainer>
        <PostBody>
          <SectionContainer>
            <HeroWrapper>
              <HeroContent>
                <h1>Top wedding venues on Zola</h1>
                <p>
                  With so many venue options available, wedding planning can feel overwhelming.
                  Hometown haunt or far-flung destination? Indoor ballroom, beach, or rustic barn?
                  All-inclusive or DIY? At Zola, we&apos;ve helped thousands of couples prepare for
                  their weddings the way they want, whether that&apos;s a big soiree with lots of
                  plus ones, or intimate elopement under the stars. We&apos;ll help you find a
                  wedding venue that fits your budget, location, and style. But first, let&apos;s
                  start with some inspiration. We&apos;ve read reviews from hundreds of couples like
                  you to find the top venues in cities across the country. Find your state
                  below—lots more coming soon!
                </p>
              </HeroContent>
              <HeroImage src={Venues} alt="Venue with set table and chairs in foreground" />
            </HeroWrapper>
            <div>
              <Subhead>Explore your state below for vendor recommendations</Subhead>
              <StateContainer>
                {sortedUniqueStates.length > 0 &&
                  sortedUniqueStates.map(state => (
                    <StateLink
                      key={toKebabCase(state)}
                      href={`/expert-advice/best-venues/${toKebabCase(state)}`}
                    >
                      {state}
                    </StateLink>
                  ))}
              </StateContainer>
            </div>
            <div>
              <CrossSellContainer />
            </div>
          </SectionContainer>
        </PostBody>
        <BreadcrumbsContainer borderTop onlyMobile>
          <BreadcrumbsV2
            currentSelectionLabel={currentSelectionLabel}
            disableBackLink
            linkItems={breadcrumbs}
          />
        </BreadcrumbsContainer>
        <ExpertAdviceFooter expertAdviceFooter={expertAdviceFooter} />
        <PreauthFooterV3 isGuest={userCtx ? Boolean(userCtx.is_guest) : true} />
      </PageWrapper>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  const [expertAdviceFooter, navHTML, userCtx, stateDataResponseObject] = await Promise.all([
    getCachedExpertAdviceFooterData(req),
    fetchNavHTML(req),
    fetchUser(req),
    fetchStateData(),
  ]);

  if (!stateDataResponseObject || !stateDataResponseObject.data) {
    logger.error('Page not found: expert-advice/best-venues');
    return { notFound: true };
  }

  return {
    props: JSON.parse(
      JSON.stringify({
        expertAdviceFooter,
        navHTML,
        userCtx,
        data: stateDataResponseObject.data,
      })
    ),
  };
};

export default BestVenuesStatePage;
